import { AlertTriangle, Check } from 'lucide-react';
import type { GuestCommunicationMessageKey } from '@/shared/api';
import type { SwitchOutcome } from './useCatalogSwitch';

/**
 * Rückmeldung zum letzten Schalter — bleibt stehen, bis der nächste kommt, statt nach wenigen
 * Sekunden zu verschwinden; darunter die feste Regel, wann Erinnerungen überhaupt gehen.
 */
export function JourneyStatus({
  outcome,
  labelFor,
  busy,
  onUndo,
}: {
  outcome: SwitchOutcome | null;
  labelFor: (key: GuestCommunicationMessageKey) => string;
  busy: boolean;
  onUndo: () => void;
}) {
  return (
    <div className="border-line/60 mt-3 space-y-1 border-t pt-2 text-xs">
      {outcome?.kind === 'saved' && (
        <p role="status" className="text-ink flex flex-wrap items-center gap-x-2">
          <Check size={14} className="text-sage shrink-0" aria-hidden />
          <span>
            „{labelFor(outcome.key)}“ ist jetzt {outcome.enabled ? 'aktiv' : 'inaktiv'}.
          </span>
          <button
            type="button"
            aria-disabled={busy || undefined}
            onClick={() => !busy && onUndo()}
            className="text-ink focus-visible:outline-sage min-h-11 rounded-sm px-1 font-semibold underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 aria-disabled:opacity-50"
          >
            Rückgängig
          </button>
        </p>
      )}
      {outcome?.kind === 'failed' && (
        <p role="alert" className="text-sealwax flex items-start gap-2 py-2">
          <AlertTriangle size={14} className="mt-px shrink-0" aria-hidden />
          <span>
            „{labelFor(outcome.key)}“{outcome.unclear ? ': ' : ' wurde nicht umgeschaltet. '}
            {outcome.reason}
          </span>
        </p>
      )}
      <p className="text-ink-soft py-1">
        <b className="text-ink font-semibold">Letzte Erinnerung:</b> Wer rechtzeitig bucht, bekommt
        sie am Tag vor der Anreise („Vortag“). Ist der Vortag bei einer kurzfristigen Buchung schon
        vorbei, kommt stattdessen „Anreisetag“ – am Anreisetag oder, falls der Gast schon da ist,
        während des Aufenthalts. Nie beide, und frühestens zwei Stunden nach der
        Willkommensnachricht.
      </p>
      <p className="text-ink-soft py-1">
        Erinnerungen gehen nur, solange der digitale Check-in offen ist. Manuelle Buchungen bekommen
        E-Mails, Airbnb- und Booking.com-Gäste OTA-Nachrichten.
      </p>
    </div>
  );
}
